import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Lock, Mail, UserCheck } from 'lucide-react';

const PrivacyPage = () => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <main data-testid="privacy-page" className="bg-gray-50 min-h-screen">
      <SEO 
        title="Privacy Policy | BBM Appliances"
        description="Privacy Policy for BBM Appliances. Learn how we collect, use, and protect your personal information."
        canonical="https://www.bbmappliances.com/privacy"
      />
      
      {/* Header */}
      <div className="bg-foreground text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold">Privacy Policy</h1>
          <p className="text-white/70 mt-4">Last Updated: {currentDate}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 space-y-10">
          
          {/* Introduction */}
          <section data-testid="privacy-intro">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-heading font-semibold">Our Commitment to Privacy</h2>
            </div>
            <p className="text-gray-600 leading-relaxed">
              BBM Appliances (DBA) is committed to protecting your privacy. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you use our website and services. 
              Please read this policy carefully to understand our practices regarding your personal data.
            </p>
          </section>

          {/* Information We Collect */}
          <section data-testid="privacy-collection">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-heading font-semibold">Information We Collect</h2>
            </div>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p><strong>Personal Information:</strong> When you book a service or contact us, we may collect:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Full name</li>
                <li>Phone number</li>
                <li>Email address</li>
                <li>Service address</li>
                <li>Appliance information and service details</li>
                <li>Payment information (processed securely through third-party providers)</li>
              </ul>
              
              <p className="mt-6"><strong>Automatically Collected Information:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>IP address and browser type</li>
                <li>Device information</li>
                <li>Pages visited and time spent on our website</li>
                <li>Referring website addresses</li>
              </ul>
            </div>
          </section>

          {/* How We Use Information */}
          <section data-testid="privacy-use">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-heading font-semibold">How We Use Your Information</h2>
            </div>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Schedule and provide appliance repair services</li>
                <li>Communicate with you about your service appointment</li>
                <li>Process payments and send receipts</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send service reminders and follow-up communications</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>
          </section>

          {/* Information Sharing */}
          <section data-testid="privacy-sharing">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-heading font-semibold">Information Sharing</h2>
            </div>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p><strong>We do NOT sell your personal information to third parties.</strong></p>
              <p className="mt-4">We may share your information with:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Service Providers:</strong> Technicians and subcontractors who perform repair services 
                  (limited to information necessary to complete the service)
                </li>
                <li>
                  <strong>Payment Processors:</strong> Secure third-party payment processing services
                </li>
                <li>
                  <strong>Communication Services:</strong> Email and SMS providers to send service notifications
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law, court order, or government regulation
                </li>
              </ul>
            </div>
          </section>

          {/* Data Security */}
          <section data-testid="privacy-security">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Lock className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-heading font-semibold">Data Security</h2>
            </div>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p>
                We implement appropriate technical and organizational security measures to protect your personal 
                information against unauthorized access, alteration, disclosure, or destruction. These measures include:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Secure, encrypted connections (HTTPS) for data transmission</li>
                <li>Limited access to personal information on a need-to-know basis</li>
                <li>Regular security assessments and updates</li>
                <li>Secure storage of customer records</li>
              </ul>
              <p className="mt-4 text-sm">
                While we strive to protect your personal information, no method of transmission over the Internet 
                or electronic storage is 100% secure. We cannot guarantee absolute security.
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section data-testid="privacy-cookies">
            <h2 className="text-xl font-heading font-semibold mb-4">Cookies & Tracking Technologies</h2>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p>
                Our website may use cookies and similar tracking technologies to enhance your browsing experience. 
                These technologies help us:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Remember your preferences</li>
                <li>Understand how you use our website</li>
                <li>Improve our services and user experience</li>
                <li>Analyze website traffic (via Google Analytics)</li>
              </ul>
              <p className="mt-4">
                You can control cookie settings through your browser preferences. Note that disabling cookies 
                may affect some website functionality.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section data-testid="privacy-rights">
            <h2 className="text-xl font-heading font-semibold mb-4">Your Rights</h2>
            <div className="text-gray-600 leading-relaxed space-y-4">
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access the personal information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your personal information</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent where processing is based on consent</li>
              </ul>
              <p className="mt-4">
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </div>
          </section>

          {/* Third-Party Links */}
          <section data-testid="privacy-thirdparty">
            <h2 className="text-xl font-heading font-semibold mb-4">Third-Party Links</h2>
            <p className="text-gray-600 leading-relaxed">
              Our website may contain links to third-party websites or services. We are not responsible for 
              the privacy practices of these external sites. We encourage you to review the privacy policies 
              of any third-party sites you visit.
            </p>
          </section>

          {/* Children's Privacy */}
          <section data-testid="privacy-children">
            <h2 className="text-xl font-heading font-semibold mb-4">Children's Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              Our services are not directed to individuals under 18 years of age. We do not knowingly collect 
              personal information from children. If you believe we have collected information from a child, 
              please contact us immediately.
            </p>
          </section>

          {/* Data Retention */}
          <section data-testid="privacy-retention">
            <h2 className="text-xl font-heading font-semibold mb-4">Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined 
              in this policy, comply with legal obligations, resolve disputes, and enforce our agreements. 
              Service records may be retained for warranty and customer service purposes.
            </p>
          </section>

          {/* Policy Changes */}
          <section data-testid="privacy-changes">
            <h2 className="text-xl font-heading font-semibold mb-4">Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. Changes will be posted on this page with 
              an updated revision date. We encourage you to review this policy periodically. Your continued 
              use of our services after any changes indicates your acceptance of the updated policy.
            </p>
          </section>

          {/* Contact Information */}
          <section data-testid="privacy-contact" className="border-t pt-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-xl font-heading font-semibold">Contact Us</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="font-medium text-gray-800">BBM Appliances</p>
              <p className="text-gray-600">Email: Hello@bbmappliances.com</p>
              <p className="text-gray-600">Phone: (855) 605-6034</p>
            </div>
          </section>

        </div>
      </div>
    </main>
  );
};

export default PrivacyPage;
